import { useState } from 'react'
import PropTypes from 'prop-types'
import { FiArrowLeft, FiTrash2, FiMinus, FiPlus } from 'react-icons/fi'
import { useCart } from '../../context/CartContext'
import { isFormStylingApplied, isButtonStylingApplied } from '../../utils/sectionFormStyling'

export default function ShoppingCart({ onCheckout, onContinueShopping, website, section, sectionFormStyling, useDefaultStyling = true, sectionType = 'cart' }) {
  const { cartItems, updateQuantity, removeFromCart, subtotal, total } = useCart()
  const [removingId, setRemovingId] = useState(null)
  const [hoveredItems, setHoveredItems] = useState(new Set())
  const isFormStyling = useDefaultStyling || isFormStylingApplied(sectionFormStyling, sectionType, section?._id)
  const isButtonStyling = useDefaultStyling || isButtonStylingApplied(sectionFormStyling, sectionType, section?._id)
  
  // Default values to fallback to if styling is not provided
  const DEFAULT_FORM_COLORS = {
    backgroundColor: '#FFFFFF',
    textColor: '#111827',
    hoverBackgroundColor: '#F9FAFB',
    hoverTextColor: '#374151',
    buttonBackgroundColor: '#3B82F6',
    buttonTextColor: '#FFFFFF',
    hoverButtonBackgroundColor: '#2563eb',
    hoverButtonTextColor: '#ffffff'
  }
  
  // Ensure we have valid styling values
  const formColors = isFormStyling ? (sectionFormStyling || DEFAULT_FORM_COLORS) : undefined
  
  const formStyle = isFormStyling ? {
    backgroundColor: formColors?.backgroundColor || DEFAULT_FORM_COLORS.backgroundColor,
    color: formColors?.textColor || DEFAULT_FORM_COLORS.textColor
  } : undefined
  const textStyle = isFormStyling ? { color: formColors?.textColor || DEFAULT_FORM_COLORS.textColor } : undefined
  const buttonStyle = isButtonStyling ? {
    backgroundColor: formColors?.buttonBackgroundColor || DEFAULT_FORM_COLORS.buttonBackgroundColor,
    color: formColors?.buttonTextColor || DEFAULT_FORM_COLORS.buttonTextColor
  } : { backgroundColor: '#3B82F6', color: '#FFFFFF' } // Always provide default button styling

  const handleRemove = (productId) => {
    setRemovingId(productId)
    setTimeout(() => {
      removeFromCart(productId)
      setRemovingId(null)
    }, 300)
  }

  const handleQuantityChange = (productId, newQuantity) => {
    const quantity = parseInt(newQuantity) || 0
    updateQuantity(productId, quantity)
  }

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8" style={formStyle}>
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <button
            onClick={onContinueShopping}
            style={buttonStyle}
            className="flex items-center gap-2 mb-8 font-medium transition-colors"
          >
            <FiArrowLeft className="w-4 h-4" />
            Continue Shopping
          </button>

          {/* Empty Cart */}
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <div className="mb-4 text-5xl">🛒</div>
            <h2 className="text-3xl font-bold mb-2" style={textStyle}>Your Cart is Empty</h2>
            <p className="text-lg mb-8" style={textStyle}>
              Add some products to get started!
            </p>
            <button
              onClick={onContinueShopping}
              style={buttonStyle}
              className="inline-block px-4 sm:px-6 md:px-8 py-2 sm:py-2.5 md:py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors text-sm sm:text-base"
            >
              Start Shopping
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8" style={formStyle}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <button
          onClick={onContinueShopping}
          style={buttonStyle}
          className="flex items-center gap-2 mb-8 font-medium transition-colors"
        >
          <FiArrowLeft className="w-4 h-4" />
          Continue Shopping
        </button>

        <h1 className="text-4xl font-bold mb-8" style={textStyle}>Shopping Cart</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cartItems.map((item) => (
              <div
                key={item._id}
                className={`rounded-lg shadow-sm overflow-hidden transition-all duration-300 ${
                  removingId === item._id ? 'opacity-50 scale-95' : 'opacity-100'
                }`}
                style={isFormStyling ? {
                  backgroundColor: hoveredItems.has(item._id) ? (sectionFormStyling.hoverBackgroundColor || sectionFormStyling.backgroundColor) : sectionFormStyling.backgroundColor,
                  color: hoveredItems.has(item._id) ? (sectionFormStyling.hoverTextColor || sectionFormStyling.textColor) : sectionFormStyling.textColor
                } : undefined}
                onMouseEnter={() => setHoveredItems(prev => new Set([...prev, item._id]))}
                onMouseLeave={() => setHoveredItems(prev => { const newSet = new Set(prev); newSet.delete(item._id); return newSet; })}
              >
                <div className="flex gap-4 p-6">
                  {/* Product Image */}
                  <div className="flex-shrink-0 w-24 h-24 bg-gray-100 rounded overflow-hidden">
                    {item.image?.url ? (
                      <img
                        src={item.image.url}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <span className="text-2xl">📦</span>
                      </div>
                    )}
                  </div>

                  {/* Product Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold truncate" style={isFormStyling ? {
                      color: hoveredItems.has(item._id) ? (sectionFormStyling.hoverTextColor || sectionFormStyling.textColor) : sectionFormStyling.textColor
                    } : undefined}>
                      {item.name}
                    </h3>
                    {item.description && (
                      <p className="text-sm line-clamp-2 mt-1" style={isFormStyling ? {
                        color: hoveredItems.has(item._id) ? (sectionFormStyling.hoverTextColor || sectionFormStyling.textColor) : sectionFormStyling.textColor
                      } : undefined}>
                        {item.description}
                      </p>
                    )}
                    <p className="font-semibold mt-2" style={isFormStyling ? {
                      color: hoveredItems.has(item._id) ? (sectionFormStyling.hoverTextColor || sectionFormStyling.textColor) : sectionFormStyling.textColor
                    } : undefined}>
                      {item.currency} {item.price.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </p>
                  </div>

                  {/* Quantity and Total */}
                  <div className="flex flex-col items-end gap-4">
                    {/* Quantity Selector */}
                    <div className="flex items-center border border-gray-300 rounded-lg">
                      <button
                        onClick={() => handleQuantityChange(item._id, item.quantity - 1)}
                        className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors"
                        title="Decrease quantity"
                      >
                        <FiMinus className="w-4 h-4" />
                      </button>
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => handleQuantityChange(item._id, e.target.value)}
                        className="w-12 text-center border-l border-r border-gray-300 py-1 focus:outline-none"
                      />
                      <button
                        onClick={() => handleQuantityChange(item._id, item.quantity + 1)}
                        className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors"
                        title="Increase quantity"
                      >
                        <FiPlus className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Subtotal */}
                    <div className="text-right">
                      <p className="text-sm mb-1" style={isFormStyling ? {
                        color: hoveredItems.has(item._id) ? (sectionFormStyling.hoverTextColor || sectionFormStyling.textColor) : sectionFormStyling.textColor
                      } : undefined}>Subtotal</p>
                      <p className="text-xl font-bold" style={isFormStyling ? {
                        color: hoveredItems.has(item._id) ? (sectionFormStyling.hoverTextColor || sectionFormStyling.textColor) : sectionFormStyling.textColor
                      } : undefined}>
                        {item.currency} {(item.price * item.quantity).toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                      </p>
                    </div>

                    {/* Remove Button */}
                    <button
                      onClick={() => handleRemove(item._id)}
                      style={buttonStyle}
                      className={`p-2 rounded transition-colors ${isButtonStyling ? '' : 'text-red-600 hover:text-red-700 hover:bg-red-50'}`}
                      title="Remove from cart"
                    >
                      <FiTrash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="rounded-lg shadow-sm p-6 sticky top-4" style={isFormStyling ? {
                backgroundColor: sectionFormStyling.backgroundColor,
                color: sectionFormStyling.textColor
              } : undefined}>
              <h2 className="text-2xl font-bold mb-6" style={textStyle}>Order Summary</h2>

              {/* Items */}
              <div className="space-y-3 mb-6 pb-6 border-b border-gray-200">
                {cartItems.map((item) => (
                  <div key={item._id} className="flex justify-between text-sm">
                    <span className="text-gray-700">
                      {item.name} <span className="text-gray-600">x{item.quantity}</span>
                    </span>
                    <span className="font-medium text-gray-900">
                      {item.currency} {(item.price * item.quantity).toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </span>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="space-y-3 mb-8">
                <div className="flex justify-between">
                  <span style={textStyle}>Subtotal</span>
                  <span className="font-medium" style={textStyle}>
                    {cartItems[0]?.currency || 'NGN'} {subtotal.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span style={textStyle}>Tax</span>
                  <span className="font-medium italic text-green-600" style={textStyle}>Tax Included</span>
                </div>
                <div className="flex justify-between pt-3 border-t border-gray-200">
                  <span className="font-semibold" style={textStyle}>Total</span>
                  <span className="text-xl font-bold text-primary-600">
                    {cartItems[0]?.currency || 'NGN'} {total.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </span>
                </div>
              </div>

              {/* Checkout Button */}
              <button
                onClick={onCheckout}
                style={buttonStyle}
                className={`w-full py-3 rounded-lg font-semibold transition-colors mb-3 ${isButtonStyling ? '' : 'hover:bg-primary-700'}`}
              >
                Proceed to Checkout
              </button>

              {/* Continue Shopping */}
              <button
                onClick={onContinueShopping}
                style={buttonStyle}
                className={`w-full py-2 border rounded-lg font-medium transition-colors ${isButtonStyling ? '' : 'hover:bg-gray-50'}`}
              >
                Continue Shopping
              </button>

              {/* Info */}
              <div className="mt-6 p-3 bg-blue-50 rounded border border-blue-200">
                <p className="text-xs text-blue-700">
                  ✓ Secure checkout powered by {website?.paymentSettings?.paymentGateway === 'stripe' ? 'Stripe' : 'Paystack'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

ShoppingCart.propTypes = {
  onCheckout: PropTypes.func,
  onContinueShopping: PropTypes.func,
  website: PropTypes.shape({
    paymentSettings: PropTypes.shape({
      paymentGateway: PropTypes.string
    })
  }),
  section: PropTypes.shape({
    _id: PropTypes.string
  }),
  sectionFormStyling: PropTypes.object,
  sectionType: PropTypes.string
}
